import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import {
  createSalesService,
  fetchSalesServiceById,
  updateSalesService
} from '../../../services/salesServiceService';

const AddEditService = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // ID for editing

  // Define form state
  const [formData, setFormData] = useState<{
    service_name: string;
    description: string;
  }>({
    service_name: '',
    description: ''
  });

  const [loading, setLoading] = useState(false); // Loading state for fetching data
  const [submitting, setSubmitting] = useState(false); // Loading state for form submission
  const [error, setError] = useState<string | null>(null); // Error handling

  // Fetch existing service details if editing
  useEffect(() => {
    if (id) {
      setLoading(true);
      const loadService = async () => {
        try {
          const service = await fetchSalesServiceById(id);
          setFormData({
            service_name: service.service_name,
            description: service.description
          });
        } catch (error) {
          setError('Failed to load service details. Please try again.');
          console.error('Error fetching service:', error);
        } finally {
          setLoading(false);
        }
      };
      loadService();
    }
  }, [id]);

  // Handle input change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Handle form submission (Create or Update)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (id) {
        await updateSalesService(id, formData);
        alert('Service updated successfully');
      } else {
        await createSalesService(formData);
        alert('Service added successfully');
      }
      navigate('/sales/services/all-services');
    } catch (error) {
      setError('Error submitting service. Please try again.');
      console.error('Error submitting service:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h2>{id ? 'Edit' : 'Add'} Service</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading service details...</p>
        </div>
      ) : (
        <Form onSubmit={handleSubmit}>
          {/* Service Name */}
          <Form.Group>
            <Form.Label>Service Name</Form.Label>
            <Form.Control
              type="text"
              name="service_name"
              value={formData.service_name}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </Form.Group>

          {/* Service Description */}
          <Form.Group>
            <Form.Label>Service Description</Form.Label>
            <Form.Control
              as="textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </Form.Group>

          {/* Submit Button */}
          <Button type="submit" className="mt-3" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              `${id ? 'Update' : 'Add'} Service`
            )}
          </Button>
        </Form>
      )}
    </div>
  );
};

export default AddEditService;
